import { addMilliseconds, format as F, formatDuration, formatISO, subMilliseconds } from 'date-fns'
import { enUS } from 'date-fns/locale'
import { formatInTimeZone, getTimezoneOffset } from 'date-fns-tz'
import { zeroPad } from 'react-countdown'

import { Config } from '../../context/config'
import { getDateLocale } from '../../services/i18n'

type DateType = Date | number | string

type Interval = {
  start: DateType
  end: DateType
}

const formatDistanceLocale: Record<string, string> = {
  xSeconds: '{{count}} s',
  xMinutes: '{{count}} m',
  xHours: '{{count}} h',
}

const shortEnLocale = {
  formatDistance: (token: string, count: string) => formatDistanceLocale[token].replace('{{count}}', count),
}

const getTodayIso = (today = new Date()) => {
  return `${today.getFullYear()}-${zeroPad(today.getMonth() + 1)}-${zeroPad(today.getDate())}`
}

/**
 * Format as "2020-12-12" - Used for backend requests.
 */
export function formatIso8601(date: DateType, timeZone?: string) {
  if (timeZone) {
    return format(date, 'yyyy-MM-dd', timeZone)
  }
  return formatISO(new Date(date), { representation: 'date' })
}

/**
 * Format as "2020-12-12T18:45:00".
 */
export function formatTodayIso(iso: string) {
  return `${getTodayIso()}T${iso.split('T').pop()}`
}

/**
 * Format as "45 min" or "1 h 45 min".
 */
export function formatShortDurationMinutes(duration: Duration) {
  return formatDuration(duration, { format: ['hours', 'minutes'], locale: shortEnLocale })
}

/**
 * Format as "12 min 45 secs" or "45 secs".
 */
export function formatShortDurationSeconds(duration: Duration) {
  return formatDuration(duration, { format: ['minutes', 'seconds'], locale: shortEnLocale })
}

/**
 * Format aa "45 minutes" or "1 hour 45 minutes".
 */
export function formatDurationMinutes(duration: Duration) {
  return formatDuration(duration, { format: ['hours', 'minutes'] })
}

/**
 * Format as "12 minutes 45 seconds" or "45 seconds".
 */
export function formatDurationSeconds(duration: Duration) {
  return formatDuration(duration, { format: ['minutes', 'seconds'] })
}

/**
 * Format as "45 minutes" or "1 hour 45 minutes".
 */
export function formatTimeInterval({ start, end }: Interval) {
  const totalMinutes = (new Date(start).getTime() - new Date(end).getTime()) / 1000 / 60

  return formatDuration({ hours: Math.floor(totalMinutes / 60), minutes: Math.floor(totalMinutes % 60) })
}

function format(date: DateType, baseFormat: string, timeZone?: string) {
  let d = new Date(date)
  if (timeZone) {
    const offset = getTimezoneOffset(timeZone, d)
    d = offset < 0 ? subMilliseconds(d, offset) : addMilliseconds(d, offset)
  }
  return F(d, baseFormat, { locale: getDateLocale() })
}

/**
 * dd/MM/yyyy - Format as "12/12/2020".
 */
export function formatDate(date: DateType, config: Config) {
  return format(date, config.partnerConfig.dateFormats.date)
}

/**
 * dd/MM/yy - Format as "12/12/20".
 */
export function formatDateShort(date: DateType, config: Config) {
  return format(date, config.partnerConfig.dateFormats.dateShort)
}

/**
 * eeee do MMMM - Format as "Saturday 12th December".
 */
export function formatDateLongMonth(date: DateType, config: Config, timeZone?: string) {
  return format(date, config.partnerConfig.dateFormats.dateLongMonth, timeZone)
}

/**
 * eee do MMMM - Format as "Sat 12th December".
 */
export function formatDateShortDayLongMonth(date: DateType, config: Config, timeZone?: string) {
  return format(date, config.partnerConfig.dateFormats.dateShortDayLongMonth, timeZone)
}

/**
 * MMMM - Format as "December".
 */
export function formatLongMonth(date: DateType, timezone?: string) {
  return format(date, 'MMMM', timezone)
}

/**
 * yyyy - Format as "2020".
 */
export function formatLongYear(date: DateType) {
  return format(date, 'yyyy')
}

/**
 * EEEE - Format as "Saturday".
 */
export function formatDayOfWeek(date: DateType) {
  return format(date, 'EEEE')
}

/**
 * dd MMMM yyyy, HH:mm - Format as "12 December 2020, 18:45".
 */
export function formatDateTimeLongMonth(date: DateType, config: Config) {
  return format(date, config.partnerConfig.dateFormats.dateTimeLongMonth)
}

/**
 * HH:mm - Format as "18:45".
 */
export function formatTimeShort(date: DateType, config: Config) {
  return format(date, config.partnerConfig.dateFormats.timeShort)
}

/**
 * HH:mm - Format as "18:45 - 19:30".
 */
export function formatShortTimeInterval({ start, end }: Interval, config: Config) {
  return [start, end].map(date => formatTimeShort(date, config)).join(' - ')
}

/**
 * z - Format as "CET".
 */
export function formatTimeZone(date: DateType) {
  return format(date, 'z')
}

/**
 * Monday - Tuesday ...
 */
export function getEnglishDayOfTheWeek(date: DateType) {
  return F(new Date(date), 'EEEE', { locale: enUS })
}

export function formatDateOfBirth(date: DateType, config: Config, timezone: string) {
  return formatInTimeZone(date, timezone, config.partnerConfig.dateFormats.date)
}
